<template>
  <div class="row px-3 pt-3">
    <div class="row px-3 m-auto col-12">
      <div class="col-12 col-md-6 justify-content-start">
        <h3 class="mt-0 pt-0 pl-0 mb-1 text-capitalize">
          {{ title }}
        </h3>
      </div>

      <div
          v-if="showMassAllocateButtons"
          class="col-12 col-md-6 d-flex flex-column flex-md-row justify-content-end"
      >
        <button class="btn btn-themed-o mb-1 mb-md-0 mr-md-1 p-1" @click="$emit('mass-allocate', true, 1)">
          {{ $t('flexben.benefit_groups.btn.add_all_ro') }}
        </button>

        <button class="btn btn-themed-o mb-1 mb-md-0 mr-md-1 p-1" @click="$emit('mass-allocate', true, -1)">
          {{ $t('flexben.benefit_groups.btn.add_all_int') }}
        </button>

        <button class="btn btn-themed-o mb-1 mb-md-0 mr-md-1 p-1" @click="$emit('mass-allocate', true)">
          {{ $t('flexben.benefit_groups.btn.add_all') }}
        </button>

        <button class="btn btn-themed-o mb-1 mb-md-0 mr-md-1 px-7 mx-8" @click="$emit('mass-allocate', false, -1)">
          {{ $t('flexben.benefit_groups.btn.remove_all_int') }}
        </button>

        <button class="btn btn-themed-o mb-1 mb-md-0 mr-md-1 px-7 mx-8" @click="$emit('mass-allocate', false)">
          {{ $t('flexben.benefit_groups.btn.remove_all') }}
        </button>
      </div>
    </div>

    <div class="col-12 mb-1">
      <div class="table-responsive scaled-table">
        <table class="table table-striped">
          <thead>
          <tr>
            <td class="w-25">{{ $t("flexben.benefit_groups.detail.ben_name") }}</td>

            <td v-if="showPartnerColumn" class="w-10">
              {{ $t("flexben.benefit_groups.detail.partner_name") }}
            </td>

            <td class="w-50 d-none d-md-table-cell">
              {{ $t("flexben.benefit_groups.detail.description") }}
            </td>

            <td class="w-25">
              {{ $t("flexben.benefit_groups.detail.allocation_control") }}
            </td>
          </tr>
          </thead>

          <tbody v-if="items.length > 0">
          <template v-for="benefit in items">
          <tr :key="benefit.id">
            <td class="w-25">
              <span
                  :style="{ backgroundColor: benefit.color }"
                  class="color-bullet"
              ></span>
              {{ benefit.name }}
            </td>

            <td v-if="showPartnerColumn" class="w-10">
              <div class="text-ellipsis-40">
                {{ benefit.partner ? benefit.partner.name : '-' }}
              </div>
            </td>

            <td class="w-50 d-none d-md-table-cell">
              <div
                  class="links-container"
                  :class="{ 'text-ellipsis-40': readMoreBenId !== benefit.id }"
                  v-html="getBenefitDescription(benefit)"
              ></div>

              <br>

              <a
                  href="#"
                  class="text-themed"
                  @click.prevent="toggleReadMore(benefit.id)"
              >
                {{
                  readMoreBenId === benefit.id
                      ? $t("flexben.general.read_less")
                      : $t("flexben.general.read_more")
                }}
              </a>
            </td>

            <td class="w-25">
              <div class="d-inline-flex">
                <b-form-checkbox
                    :id="'benefit_toggle_' + benefit.id"
                    switch
                    :checked="formData.benefit_ids.includes(benefit.id)"
                    @change="$emit('toggle-benefit-id', benefit.id)"
                />

                <span class="ml-3 my-auto">
                  {{ $t("flexben.benefit_groups.detail." + (formData.benefit_ids.includes(benefit.id) ? 'allocated' : 'not_allocated')) }}
                </span>
              </div>
            </td>
          </tr>
          <tr
              v-if="isCashOutBenefit(benefit)"
              :key="benefit.id + '_cash_out_schedule'"
              class="cashout-schedule-row"
          >
            <td :colspan="showPartnerColumn ? 4 : 3" class="bg-white">
              <div class="cashout-schedule-card p-3">
                <div class="d-flex flex-column flex-lg-row justify-content-between align-items-lg-start mb-2">
                  <div>
                    <div class="font-weight-bold">{{ $t("flexben.benefit_groups.detail.cash_out_schedule_title") }}</div>
                    <div class="text-muted small">
                      {{
                        isCashOutAllocated(benefit)
                          ? $t("flexben.benefit_groups.detail.cash_out_schedule_hint")
                          : $t("flexben.benefit_groups.detail.cash_out_schedule_disabled_hint")
                      }}
                    </div>
                  </div>
                  <button
                      v-if="isCashOutAllocated(benefit)"
                      type="button"
                      class="btn text-themed px-0 mt-2 mt-lg-0"
                      @click="$emit('reset-cash-out-schedule')"
                  >
                    <font-awesome-icon icon="undo" class="mr-1"/>
                    {{ $t('flexben.benefit_groups.detail.cash_out_schedule_reset') }}
                  </button>
                </div>

                <div class="row">
                  <div class="col-12 col-md-6 col-xl-4 mb-2">
                    <label :for="'cash_out_start_at_' + benefit.id" class="col-form-label">
                      {{ $t("flexben.benefit_groups.detail.cash_out_start_date") }}
                    </label>
                    <div class="d-inline-flex w-100">
                      <font-awesome-icon icon="calendar-alt" class="my-auto mr-2 text-24 flx-calendar-icon"/>
                      <datepicker
                          :id="'cash_out_start_at_' + benefit.id"
                          :format="dateFormat"
                          :inputClass="'form-control-d m-0 w-100 background-white' + (Object.keys(formErrors).includes('cash_out_start_at') ? ' border-danger' : '')"
                          class="w-100"
                          :disabled="!isCashOutAllocated(benefit)"
                          :value="parseLocalDate(cashOutStartLocal)"
                          @input="onCashOutStartChange"
                      />
                    </div>
                  </div>

                  <div class="col-12 col-md-6 col-xl-4 mb-2">
                    <label :for="'cash_out_end_at_' + benefit.id" class="col-form-label">
                      {{ $t("flexben.benefit_groups.detail.cash_out_end_date") }}
                    </label>
                    <div class="d-inline-flex w-100">
                      <font-awesome-icon icon="calendar-alt" class="my-auto mr-2 text-24 flx-calendar-icon"/>
                      <datepicker
                          :id="'cash_out_end_at_' + benefit.id"
                          :format="dateFormat"
                          :inputClass="'form-control-d m-0 w-100 background-white' + (Object.keys(formErrors).includes('cash_out_end_at') ? ' border-danger' : '')"
                          class="w-100"
                          :disabled="!isCashOutAllocated(benefit)"
                          :value="parseLocalDate(cashOutEndLocal)"
                          @input="onCashOutEndChange"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </td>
          </tr>
          </template>
          </tbody>

          <tbody v-else>
          <tr>
            <td
                :colspan="showPartnerColumn ? 4 : 3"
                class="text-center trhover gri-cell"
            >
              {{ $t("flexben.benefit_groups.no_benefits_available", {
              type: $t(emptyTypeKey)
              }) }}
            </td>
          </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import { BFormCheckbox } from "bootstrap-vue";
import Datepicker from "vuejs-datepicker";
import moment from "moment";

const CASH_OUT_SUB_TYPE = "cash_out";

export default {
  name: "BenefitsTable",

  components: {
    "b-form-checkbox": BFormCheckbox,
    datepicker: Datepicker,
  },

  props: {
    title: {
      type: String,
      required: true,
    },
    items: {
      type: Array,
      default: function () {
        return [];
      },
    },
    currentLang: {
      type: String,
      default: "ro",
    },
    formData: {
      type: Object,
      required: true,
    },
    formErrors: {
      type: Object,
      default: function () {
        return {};
      },
    },
    cashOutStartLocal: {
      type: String,
      default: "",
    },
    cashOutEndLocal: {
      type: String,
      default: "",
    },
    emptyTypeKey: {
      type: String,
      default: "flexben.benefit_groups.benefit_type_flexible",
    },
    showMassAllocateButtons: {
      type: Boolean,
      default: false,
    },
    showPartnerColumn: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      readMoreBenId: null,
      dateFormat: "dd/MM/yyyy",
    };
  },

  watch: {
    items: {
      immediate: true,
      handler() {
        this.readMoreBenId = null;
      },
    },
  },

  methods: {
    isCashOutBenefit(benefit) {
      return benefit.sub_type === CASH_OUT_SUB_TYPE;
    },

    isCashOutAllocated(benefit) {
      return this.formData.benefit_ids.includes(benefit.id);
    },

    parseLocalDate(value) {
      if (!value) {
        return null;
      }

      const parsed = moment(value, "YYYY-MM-DD", true);
      return parsed.isValid() ? parsed.toDate() : null;
    },

    formatLocalDate(value) {
      if (!value) {
        return "";
      }

      const parsed = moment(value);
      return parsed.isValid() ? parsed.format("YYYY-MM-DD") : "";
    },

    onCashOutStartChange(value) {
      this.$emit("update-cash-out-start", this.formatLocalDate(value));
    },

    onCashOutEndChange(value) {
      this.$emit("update-cash-out-end", this.formatLocalDate(value));
    },

    getBenefitDescription(benefit) {
      if (!benefit.description) {
        return "";
      }

      return benefit.description[this.currentLang] || "";
    },

    toggleReadMore(benefitId) {
      this.readMoreBenId = this.readMoreBenId === benefitId ? null : benefitId;
    },
  },
};
</script>

<style scoped>
.cashout-schedule-row td {
  border-top: none;
}

.cashout-schedule-card {
  border: 1px solid #e9ecef;
  border-radius: 8px;
  background: #f8f9fa;
}
</style>
